import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { PageIntro, Section } from '@/components/PageLayout'

const buildingSizes = [
  { size: '2–4 units', financing: 'Residential mortgage', role: 'Current acquisitions' },
  { size: '5–12 units', financing: 'Commercial loan', role: 'As the portfolio grows' },
]

const stressTests = [
  {
    title: 'Vacancy',
    body: 'Income is modeled with longer vacancy between tenants than the seller’s figures or local averages suggest.',
  },
  {
    title: 'Interest rates',
    body: 'Debt service is modeled at higher interest rates than the loan being quoted.',
  },
  {
    title: 'Maintenance and capital work',
    body: 'Repair and deferred-maintenance costs are budgeted above the seller’s estimates, with a reserve for work the inspection may not reveal.',
  },
]

export default function StrategyPage() {
  return (
    <div className="bg-background text-foreground">
      <PageIntro title="Investment strategy">
        <p>Residential multifamily property, underwritten against adverse scenarios and held for the long term.</p>
      </PageIntro>

      <Section title="Market">
        <div className="text-fluid-body-lg max-w-2xl space-y-5">
          <p>
            The firm&rsquo;s current market is Cleveland, Ohio. Purchase prices there remain low relative to rents,
            which allows a property to cover its operating costs and debt service from existing income rather than from
            projected rent growth or appreciation.
          </p>
          <p>
            Within the city, the firm buys in established residential neighborhoods with steady rental demand. Each
            acquisition is underwritten to a cash-on-cash return of 8&ndash;12% at current rents.
          </p>
        </div>
        <Link to="/resources/cleveland-overview" className="prose-link mt-6 inline-block">
          Cleveland market overview
        </Link>
      </Section>

      <Section title="Asset type">
        <p className="text-fluid-body-lg mb-8 max-w-2xl">
          Residential multifamily buildings of two to twelve units: large enough to spread vacancy across several
          households, small enough to underwrite and inspect unit by unit.
        </p>
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-navy text-muted-foreground border-b text-sm">
              <th scope="col" className="py-3 pr-4 font-medium">
                Building size
              </th>
              <th scope="col" className="py-3 pr-4 font-medium">
                Typical financing
              </th>
              <th scope="col" className="py-3 font-medium">
                In the portfolio
              </th>
            </tr>
          </thead>
          <tbody>
            {buildingSizes.map((b) => (
              <tr key={b.size} className="border-border border-b">
                <th scope="row" className="text-navy py-4 pr-4 font-semibold">
                  {b.size}
                </th>
                <td className="py-4 pr-4">{b.financing}</td>
                <td className="py-4">{b.role}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section title="Underwriting" tinted>
        <p className="text-fluid-body-lg mb-8 max-w-2xl">
          Before an offer is made, the property is modeled under three adverse conditions. If it does not cover its
          costs in the downside case, the firm does not buy it.
        </p>
        <div className="grid gap-x-10 gap-y-8 sm:grid-cols-3">
          {stressTests.map((t) => (
            <div key={t.title} className="border-navy/25 border-t pt-4">
              <h3 className="text-navy mb-2 font-semibold">{t.title}</h3>
              <p className="text-foreground/85 text-[0.95rem] leading-relaxed">{t.body}</p>
            </div>
          ))}
        </div>
        <Link to="/resources/underwriting-basics" className="prose-link mt-8 inline-block">
          How the firm underwrites a deal
        </Link>
      </Section>

      <Section title="Operations">
        <dl className="max-w-2xl space-y-7">
          <div>
            <dt className="text-navy mb-1.5 font-semibold">Hold period</dt>
            <dd className="text-foreground/85 leading-relaxed">
              Properties are bought to be held for five to ten years or longer. The firm does not buy to resell.
            </dd>
          </div>
          <div>
            <dt className="text-navy mb-1.5 font-semibold">Renovation scope</dt>
            <dd className="text-foreground/85 leading-relaxed">
              Light to moderate: repairs and improvements that affect rent, safety, or the life of the building. The
              firm avoids speculative renovation and over-improvement.
            </dd>
          </div>
          <div>
            <dt className="text-navy mb-1.5 font-semibold">Management</dt>
            <dd className="text-foreground/85 leading-relaxed">
              Day-to-day leasing, rent collection, and maintenance are handled by Progressive Urban Property Management.
              The firm sets budgets, approves capital work, and oversees performance.
            </dd>
          </div>
        </dl>
      </Section>

      <section className="bg-muted">
        <div className="mx-auto flex max-w-5xl flex-col gap-6 px-5 py-14 md:flex-row md:items-center md:justify-between md:px-8">
          <p className="text-fluid-h3 text-navy max-w-xl">How investors participate in each property.</p>
          <Button asChild size="lg" className="shrink-0">
            <Link to="/invest">Investing with Fairmeadow</Link>
          </Button>
        </div>
      </section>
    </div>
  )
}
